
import React, { useState, useEffect } from 'react';

interface DebugElement {
  id: string;
  rect: DOMRect;
}

interface DebugOverlayProps {
  level: number;
}

type DebugCategory = 'rhythm' | 'sound' | 'extra' | 'gigmode';

interface DebugElement {
  id: string;
  rect: DOMRect;
  category?: DebugCategory;
}

const DebugOverlay: React.FC<DebugOverlayProps> = ({ level }) => {
  const [elements, setElements] = useState<DebugElement[]>([]);  const getCategoryFromId = (id: string): DebugCategory | undefined => {
    const [prefix, component] = id.split(':');
    if (prefix !== `Level-${level}`) return undefined;

    // Categorize components based on their names and level
    const lowerComponent = component?.toLowerCase() || '';
    
    // LEVEL 1: Big structures only
    if (level === 1) {
      if (lowerComponent.includes('chartgrid') || lowerComponent.includes('controls') || 
          lowerComponent.includes('transport') || lowerComponent.includes('midipanel') ||
          lowerComponent.includes('chordpalette') || lowerComponent.includes('rightdock')) {
        return 'rhythm'; // Green for main UI areas
      }
      return undefined; // Hide everything else at Level 1
    }
    
    // LEVEL 2: Medium structures  
    if (level === 2) {
      if (lowerComponent.includes('measure-') && !lowerComponent.includes('header') && 
          !lowerComponent.includes('chord') && !lowerComponent.includes('lyrics')) {
        return 'rhythm'; // Green for measure containers
      }
      if (lowerComponent.includes('chordarea') || lowerComponent.includes('chordcell')) {
        return 'sound'; // Blue for chord areas
      }
      if (lowerComponent.includes('lyrics') && !lowerComponent.includes('container') && 
          !lowerComponent.includes('text')) {
        return 'extra'; // Orange for lyrics areas
      }
      if (lowerComponent.includes('annotations') && !lowerComponent.includes('container') && 
          !lowerComponent.includes('row') && !lowerComponent.includes('marker')) {
        return 'gigmode'; // Purple for annotation areas
      }
      return undefined; // Hide fine details at Level 2
    }
    
    // LEVEL 3: Detail structures - but organized by component type with unique colors
    if (level === 3) {
      // Annotation details (Purple shades)
      if (lowerComponent.includes('annotation') || lowerComponent.includes('ending') || 
          lowerComponent.includes('segno') || lowerComponent.includes('coda') || 
          lowerComponent.includes('dalcapo') || lowerComponent.includes('dalsegno') || 
          lowerComponent.includes('fine') || lowerComponent.includes('tocoda') ||
          lowerComponent.includes('section') || lowerComponent.includes('rehearsal')) {
        return 'gigmode'; // Purple for annotations
      }
      // Chord details (Blue shades)  
      if (lowerComponent.includes('chord') || lowerComponent.includes('gig') || 
          lowerComponent.includes('grid') || lowerComponent.includes('divider')) {
        return 'sound'; // Blue for chords
      }
      // Lyrics details (Orange shades)
      if (lowerComponent.includes('lyrics') || lowerComponent.includes('text')) {
        return 'extra'; // Orange for lyrics
      }
      // Structure details (Green shades)
      if (lowerComponent.includes('measure') || lowerComponent.includes('header') || 
          lowerComponent.includes('repeat') || lowerComponent.includes('content') ||
          lowerComponent.includes('spacer') || lowerComponent.includes('number')) {
        return 'rhythm'; // Green for structure
      }
      return 'gigmode'; // Default purple for Level-3
    }
    
    return undefined;
    
    // Set List components are part of performance workflow (rhythm category)
    if (lowerComponent.includes('setlist') || 
        lowerComponent.includes('song') ||
        lowerComponent.includes('prev') ||
        lowerComponent.includes('next') ||
        lowerComponent.includes('navigation') ||
        lowerComponent.includes('current') ||
        lowerComponent.includes('clear')) {
      return 'rhythm';
    }
    
    if (lowerComponent.includes('transport') || 
        lowerComponent.includes('metronome') || 
        lowerComponent.includes('tempo') ||
        lowerComponent.includes('rhythm') ||
        lowerComponent.includes('beat') ||
        lowerComponent.includes('measure')) {
      return 'rhythm';
    }
    
    if (lowerComponent.includes('audio') || 
        lowerComponent.includes('sound') || 
        lowerComponent.includes('midi') ||
        lowerComponent.includes('volume') ||
        lowerComponent.includes('playback') ||
        lowerComponent.includes('chord')) {
      return 'sound';
    }
    
    // Everything else in level 2 falls under 'extra'
    return 'extra';
  };

  const getCategoryColor = (category?: DebugCategory) => {
    switch (category) {
      case 'rhythm':
        return { 
          border: '#22c55e', 
          bg: 'rgba(34, 197, 94, 0.1)', 
          label: '#16a34a'
        }; // Simple green
      case 'sound':
        return { 
          border: '#3b82f6', 
          bg: 'rgba(59, 130, 246, 0.1)', 
          label: '#2563eb'
        }; // Simple blue
      case 'extra':
        return { 
          border: '#f97316', 
          bg: 'rgba(249, 115, 22, 0.1)', 
          label: '#ea580c'
        }; // Simple orange
      case 'gigmode':
        return { 
          border: '#a855f7', 
          bg: 'rgba(168, 85, 247, 0.1)', 
          label: '#9333ea'
        }; // Simple purple
      default:
        return { 
          border: '#6b7280', 
          bg: 'rgba(107, 114, 128, 0.1)', 
          label: '#4b5563'
        }; // Simple gray
    }
  };

  useEffect(() => {
    const findElements = () => {
      const debugElements = Array.from(
        document.querySelectorAll(`[data-debug-id^="Level-${level}:"]`)
      );

      const elementData: DebugElement[] = debugElements.map((element) => {
        const id = element.getAttribute('data-debug-id') || '';
        const rect = element.getBoundingClientRect();
        const category = getCategoryFromId(id);

        return {
          id,
          rect,
          category,
        };
      });

      setElements(elementData);
    };    // Initial find
    findElements();

    // Re-find on resize and scroll for accuracy
    window.addEventListener('resize', findElements);
    window.addEventListener('scroll', findElements, true);

    // Also check periodically for changes that don't fire resize/scroll (e.g., palette opening)
    const intervalId = setInterval(findElements, 500);

    return () => {
      window.removeEventListener('resize', findElements);
      window.removeEventListener('scroll', findElements, true);
      clearInterval(intervalId);
    };
  }, [level]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    >
      {/* Debug Level Indicator */}
      <div
        style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          color: 'white',
          padding: '6px 12px',
          fontSize: '12px',
          fontFamily: 'monospace',
          fontWeight: 'bold',
          borderRadius: '6px',
          zIndex: 10001,
          border: '2px solid #00ff88',
        }}
      >
        DEBUG LEVEL {level} ({elements.length} elements)
      </div>

      {/* Color Legend for All Levels */}
      <div
        style={{
          position: 'fixed',
          top: '60px',
          right: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          color: 'white',
          padding: '12px',
          fontSize: '10px',
          fontFamily: 'monospace',
          borderRadius: '6px',
          zIndex: 10001,
          border: '1px solid #555',
          backdropFilter: 'blur(2px)',
        }}
      >
        <div style={{ marginBottom: '8px', fontWeight: 'bold', color: '#00ff88' }}>
          � LEVEL {level} - {level === 1 ? 'BIG STRUCTURES' : level === 2 ? 'MEDIUM STRUCTURES' : 'DETAIL STRUCTURES'}
        </div>
        
        {level === 1 && (
          <div>
            <div style={{ color: '#22c55e', marginBottom: '2px' }}>🟢 Main UI Areas</div>
            <div style={{ fontSize: '8px', color: '#888', marginTop: '4px' }}>
              ChartGrid, Controls, Transport, etc.
            </div>
          </div>
        )}
        
        {level === 2 && (
          <div>
            <div style={{ color: '#22c55e', marginBottom: '2px' }}>🟢 Measure Containers</div>
            <div style={{ color: '#3b82f6', marginBottom: '2px' }}>🔵 Chord Areas</div>
            <div style={{ color: '#f97316', marginBottom: '2px' }}>🟠 Lyrics Areas</div>
            <div style={{ color: '#a855f7', marginBottom: '2px' }}>🟣 Annotation Areas</div>
          </div>
        )}
        
        {level === 3 && (
          <div>
            <div style={{ color: '#22c55e', marginBottom: '2px' }}>🟢 Structure Details (unique shades)</div>
            <div style={{ color: '#3b82f6', marginBottom: '2px' }}>🔵 Chord Details (unique shades)</div>
            <div style={{ color: '#f97316', marginBottom: '2px' }}>🟠 Lyrics Details (unique shades)</div>
            <div style={{ color: '#a855f7', marginBottom: '2px' }}>🟣 Annotation Details (unique shades)</div>
            <div style={{ fontSize: '8px', color: '#888', marginTop: '6px' }}>
              Each element has unique color variation<br/>
              Dashed lines connect labels to elements
            </div>
          </div>
        )}
      </div>

      {elements.map(({ id, rect, category }, index) => {
        const colors = getCategoryColor(category);
        
        // Extract meaningful component name from debug ID
        const cleanId = id.replace(`Level-${level}:`, '');
        
        // Keep more context for unique identification
        let displayId;
        if (cleanId.includes('-')) {
          // For components with numbers/indices, keep them: "ChordCell-1-2", "Measure-3"
          const parts = cleanId.split('-');
          if (parts.length >= 2) {
            displayId = `${parts[0]}-${parts[1]}`;
            // Add third part if it exists and is a number (for chord indices)
            if (parts[2] && /^\d+$/.test(parts[2])) {
              displayId += `-${parts[2]}`;
            }
          } else {
            displayId = parts[0];
          }
        } else {
          // Simple component name
          displayId = cleanId;
        }
        const labelStyle: React.CSSProperties = {
          position: 'absolute',
          top: '2px',
          left: '2px',
          backgroundColor: colors.label,
          color: 'white',
          padding: '1px 4px',
          fontSize: '8px',
          fontFamily: 'monospace',
          fontWeight: 'bold',
          borderRadius: '2px',
          opacity: 0.8,
          zIndex: 10000 + index,
          maxWidth: '80px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        };

        return (
          <div
            key={id}
            style={{
              position: 'absolute',
              top: `${rect.top}px`,
              left: `${rect.left}px`,
              width: `${rect.width}px`,
              height: `${rect.height}px`,
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.bg,
              pointerEvents: 'none',
              boxSizing: 'border-box',
            }}
          >
            <span style={labelStyle}>
              {displayId}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default DebugOverlay;